// src/components/OnboardingModal.tsx
// Merged onboarding flow (docs/onboarding/v1-spec.md): 2 question steps +
// the module-overview welcome step (absorbed from the old WelcomeModal).
// Modes:
//   full      — questions then welcome (new authed users, onboarding_status=pending)
//   welcome   — welcome step only (guests; no profile to collect)
//   questions — questions only (one-time /paths re-prompt for skippers)

import React, { useState } from 'react';
import { onboardingApi } from '../services/api';

export type OnboardingMode = 'full' | 'welcome' | 'questions';

interface OnboardingModalProps {
  mode: OnboardingMode;
  onClose: () => void;
}

const PERSONAS = [
  { key: 'student', label: 'College / bootcamp student' },
  { key: 'career_switcher', label: 'Career switcher moving into tech' },
  { key: 'working_dev', label: 'Working developer' },
  { key: 'hobbyist', label: 'Hobbyist / self-learner' },
];

const EXPERIENCE = [
  { key: 'beginner', label: 'Complete beginner' },
  { key: 'basics', label: 'I know the basics of one language' },
  { key: 'experienced', label: 'Comfortable dev picking up something new' },
];

const GOALS = [
  { key: 'placement_prep', label: 'Placement / interview prep' },
  { key: 'career_switch', label: 'Switching careers into tech' },
  { key: 'work_stack', label: 'New language or stack for work' },
  { key: 'portfolio', label: 'Side projects & portfolio' },
];

const INTERESTS = [
  { key: 'python', label: 'Python' },
  { key: 'javascript', label: 'JavaScript' },
  { key: 'java', label: 'Java' },
  { key: 'sql', label: 'SQL & data analysis' },
  { key: 'go', label: 'Go' },
  { key: 'ai_ml', label: 'AI/ML' },
  { key: 'web_dev', label: 'Web development' },
  { key: 'not_sure', label: 'Not sure yet' },
];

const OnboardingModal: React.FC<OnboardingModalProps> = ({ mode, onClose }) => {
  // Step indices: 0 = persona+experience, 1 = goal+interests, 2 = welcome.
  const [step, setStep] = useState(mode === 'welcome' ? 2 : 0);
  const [persona, setPersona] = useState<string | null>(null);
  const [experience, setExperience] = useState<string | null>(null);
  const [goal, setGoal] = useState<string | null>(null);
  const [interests, setInterests] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);

  const finishQuestions = () => {
    if (mode === 'questions') onClose();
    else setStep(2);
  };

  const handleSkip = async () => {
    // Fire-and-forget: never trap the user in the modal on a network error.
    try { await onboardingApi.skip(); } catch { /* fail open */ }
    finishQuestions();
  };

  const handleSubmit = async () => {
    setSaving(true);
    try {
      await onboardingApi.submit({
        persona,
        experience_level: experience,
        primary_goal: goal,
        interests,
      });
    } catch { /* fail open — answers are a nice-to-have, not a gate */ }
    setSaving(false);
    finishQuestions();
  };

  const toggleInterest = (key: string) =>
    setInterests((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
    );

  const optionButton = (selected: boolean) =>
    `w-full text-left px-4 py-2.5 rounded-lg border text-sm transition-colors ${
      selected
        ? 'border-primary bg-primary/10 text-foreground font-semibold'
        : 'border-border/50 text-muted-foreground hover:border-primary/50 hover:text-foreground'
    }`;

  const chipButton = (selected: boolean) =>
    `px-3 py-1.5 rounded-full border text-xs transition-colors ${
      selected
        ? 'border-primary bg-primary/10 text-foreground font-semibold'
        : 'border-border/50 text-muted-foreground hover:border-primary/50 hover:text-foreground'
    }`;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm animate-fade-in">
      <div className="bg-card border border-border/50 rounded-2xl shadow-2xl max-w-lg w-full mx-4 overflow-hidden max-h-[90vh] overflow-y-auto">
        {step === 0 && (
          <>
            <div className="px-6 pt-6 pb-4 border-b border-border/30">
              <h2 className="text-xl font-bold text-foreground">Welcome to Cubemate</h2>
              <p className="text-sm text-muted-foreground mt-1">
                4 quick questions so your learning paths fit you — takes 30 seconds.
              </p>
            </div>
            <div className="px-6 py-5 space-y-5">
              <div>
                <h3 className="text-sm font-semibold text-foreground mb-2">Which best describes you?</h3>
                <div className="space-y-2">
                  {PERSONAS.map((o) => (
                    <button key={o.key} onClick={() => setPersona(o.key)} className={optionButton(persona === o.key)}>
                      {o.label}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <h3 className="text-sm font-semibold text-foreground mb-2">Where are you with coding?</h3>
                <div className="space-y-2">
                  {EXPERIENCE.map((o) => (
                    <button key={o.key} onClick={() => setExperience(o.key)} className={optionButton(experience === o.key)}>
                      {o.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>
            <div className="px-6 py-4 border-t border-border/30 flex items-center justify-between">
              <button onClick={handleSkip} className="text-xs text-muted-foreground hover:text-foreground transition-colors">
                I'll do this later
              </button>
              <button
                onClick={() => setStep(1)}
                className="px-5 py-2 text-sm font-semibold rounded-lg bg-primary text-primary-foreground hover:opacity-90 transition-opacity"
              >
                Next
              </button>
            </div>
          </>
        )}

        {step === 1 && (
          <>
            <div className="px-6 pt-6 pb-4 border-b border-border/30">
              <h2 className="text-xl font-bold text-foreground">Almost there</h2>
              <p className="text-sm text-muted-foreground mt-1">What are you here to do?</p>
            </div>
            <div className="px-6 py-5 space-y-5">
              <div>
                <h3 className="text-sm font-semibold text-foreground mb-2">What's your main goal right now?</h3>
                <div className="space-y-2">
                  {GOALS.map((o) => (
                    <button key={o.key} onClick={() => setGoal(o.key)} className={optionButton(goal === o.key)}>
                      {o.label}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <h3 className="text-sm font-semibold text-foreground mb-2">What do you want to learn first?</h3>
                <p className="text-xs text-muted-foreground mb-2">Pick as many as you like.</p>
                <div className="flex flex-wrap gap-2">
                  {INTERESTS.map((o) => (
                    <button key={o.key} onClick={() => toggleInterest(o.key)} className={chipButton(interests.includes(o.key))}>
                      {o.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>
            <div className="px-6 py-4 border-t border-border/30 flex items-center justify-between">
              <button onClick={handleSkip} className="text-xs text-muted-foreground hover:text-foreground transition-colors">
                I'll do this later
              </button>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setStep(0)}
                  className="px-4 py-2 text-sm font-semibold rounded-lg border border-border/50 text-muted-foreground hover:text-foreground transition-colors"
                >
                  Back
                </button>
                <button
                  onClick={handleSubmit}
                  disabled={saving}
                  className="px-5 py-2 text-sm font-semibold rounded-lg bg-primary text-primary-foreground hover:opacity-90 transition-opacity disabled:opacity-50"
                >
                  {saving ? 'Saving…' : mode === 'questions' ? 'Done' : 'Next'}
                </button>
              </div>
            </div>
          </>
        )}

        {step === 2 && (
          <>
            <div className="px-6 pt-6 pb-4 border-b border-border/30">
              <h2 className="text-xl font-bold text-foreground">Welcome to Cubemate</h2>
              <p className="text-sm text-muted-foreground mt-1">Code. Learn. Build. with AI</p>
            </div>
            <div className="px-6 py-5 space-y-5">
              <div className="flex items-start gap-3">
                <div className="flex-shrink-0 w-9 h-9 rounded-lg bg-blue-500/15 flex items-center justify-center text-blue-400 text-lg">
                  &#128187;
                </div>
                <div>
                  <h3 className="text-sm font-semibold text-foreground">Chat</h3>
                  <p className="text-xs text-muted-foreground mt-0.5">Write, explain, and run code in any language. Every answer includes executable code.</p>
                  <p className="text-xs text-primary mt-1 italic">"What is a JavaScript closure?"</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="flex-shrink-0 w-9 h-9 rounded-lg bg-purple-500/15 flex items-center justify-center text-purple-400 text-lg">
                  &#128218;
                </div>
                <div>
                  <h3 className="text-sm font-semibold text-foreground">BookMate</h3>
                  <p className="text-xs text-muted-foreground mt-0.5">Side-by-side language tutorials. Learn a new language by comparing it with one you know.</p>
                  <p className="text-xs text-primary mt-1 italic">"How does error handling work in Python vs Java?"</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="flex-shrink-0 w-9 h-9 rounded-lg bg-cyan-500/15 flex items-center justify-center text-cyan-400 text-lg">
                  &#128202;
                </div>
                <div>
                  <h3 className="text-sm font-semibold text-foreground">Workflow</h3>
                  <p className="text-xs text-muted-foreground mt-0.5">One prompt to search, download datasets, create CSVs, and generate charts.</p>
                  <p className="text-xs text-primary mt-1 italic">"Download the Iris dataset and create a scatter plot colored by species"</p>
                </div>
              </div>
            </div>
            <div className="px-6 py-4 border-t border-border/30 flex justify-end">
              <button
                onClick={onClose}
                className="px-5 py-2 text-sm font-semibold rounded-lg bg-primary text-primary-foreground hover:opacity-90 transition-opacity"
              >
                Get Started
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default OnboardingModal;
